import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton, CircularProgress, Tooltip } from "@mui/material";
import { GetZone, PostZone, DeleteZone } from "../../../Service/_index";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import MasterHistoryButton from "../../../UiComponents/MasterHistory/MasterHistoryButton";
import { upperCaseChange, preserveCaret } from "../../Policy/OnlinePolicy/utils/inputHelpers";

/**
 * Manage zones and their rates.
 *
 * One row per zone — "Zone A", "Zone B" to start with, any number later. An
 * RTO City Location points at one of these by id, so the rate lives in exactly
 * one place and a change here applies to every RTO using that zone.
 *
 * Existing zones are listed alongside the form so it is obvious what already
 * exists before adding another.
 */
const AddZoneDialog = ({ onClose, onSaved }) => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({ zoneName: "", rate: "" });
  const [error, setError] = useState("");

  const setFieldValue = (field, value) =>
    setValues((previous) => ({ ...previous, [field]: value }));

  const loadZones = async () => {
    setLoading(true);
    try {
      const res = await GetZone();
      const rows = Array.isArray(res?.data) ? res.data : [];
      setZones(rows.filter((z) => z.isActive !== false));
    } catch (e) {
      ToastError("Failed to load zones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadZones();
  }, []);

  const handleSave = async () => {
    setError("");

    const name = values.zoneName.trim();
    if (!name) {
      setError("Zone name is required");
      return;
    }
    if (values.rate === "") {
      setError("Rate is required");
      return;
    }

    // Two zones with the same name would be indistinguishable in the RTO
    // dropdown, so block it here rather than creating a confusing duplicate.
    const clash = zones.find(
      (z) => String(z.zoneName || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (clash) {
      setError(`"${clash.zoneName}" already exists.`);
      return;
    }

    setSaving(true);
    try {
      const res = await PostZone({ zoneName: name, rate: Number(values.rate) });
      if (res && !res.error) {
        setValues({ zoneName: "", rate: "" });
        await loadZones();
        if (onSaved) onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zone) => {
    const res = await DeleteZone(zone._id);
    if (res && !res.error) {
      await loadZones();
      if (onSaved) onSaved();
    }
  };

  return (
    <Box sx={{ pt: 3, px: 3, pb: 0, minWidth: 520 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Zones</Typography>
          <Typography sx={{ fontSize: 12, color: "#667085" }}>
            Each RTO City Location points at a zone; its rate drives the premium.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Add form — boxed so it reads as one unit rather than loose fields */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: "1px solid #eaecf0",
          borderRadius: "8px",
          backgroundColor: "#fcfdff",
        }}
      >
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6}>
            <Typography sx={{ mb: 0.75, fontWeight: 500, fontSize: 12.5 }}>
              Zone Name *
            </Typography>
            <input
              className="textField w-100"
              value={values.zoneName}
              placeholder="e.g. ZONE A"
              onChange={upperCaseChange(setFieldValue, "zoneName")}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography sx={{ mb: 0.75, fontWeight: 500, fontSize: 12.5 }}>
              Rate *
            </Typography>
            <input
              className="textField w-100"
              value={values.rate}
              placeholder="1.708"
              // Rates are DECIMALS (1.708, 1.676) — a digits-only filter
              // stripped the point and turned 1.708 into 1708.
              onChange={(e) => {
                const raw = e.target.value || "";
                const numeric = raw
                  .replace(/[^0-9.]/g, "")
                  .replace(/(\..*)\./g, "$1");
                preserveCaret(e.target, raw, numeric);
                setFieldValue("rate", numeric);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            {/* Explicit brand styling: the Dialog_Save_Exit class only renders
                correctly inside .Dialog_Footer, so used here it came out as a
                bare blue text link. */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                textTransform: "none",
                backgroundColor: "#1E3A5F",
                height: 40,
                "&:hover": { backgroundColor: "#162c47" },
              }}
            >
              {saving ? "Adding..." : "Add"}
            </Button>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Typography sx={{ color: "#d32f2f", fontSize: 12.5 }}>
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Existing zones */}
      <Box sx={{ mt: 2.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1 }}>
          Existing zones{zones.length ? ` (${zones.length})` : ""}
        </Typography>

        <Box
          sx={{
            border: "1px solid #eaecf0",
            borderRadius: "8px",
            overflow: "hidden",
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : zones.length === 0 ? (
            <Typography
              sx={{ fontSize: 12.5, color: "#667085", textAlign: "center", py: 3 }}
            >
              No zones yet — add one above.
            </Typography>
          ) : (
            zones.map((z, index) => (
              <Box
                key={z._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderTop: index === 0 ? "none" : "1px solid #f0f2f5",
                  "&:hover": { backgroundColor: "#fafbfc" },
                }}
              >
                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#101828" }}>
                  {z.zoneName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#1E3A5F",
                    backgroundColor: "#1E3A5F14",
                    border: "1px solid #1E3A5F26",
                    borderRadius: "999px",
                    px: 1.25,
                    py: 0.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  Rate {z.rate ?? "--"}
                </Typography>
                <MasterHistoryButton
                  entityType="zone"
                  entityId={z._id}
                  recordLabel={z.zoneName}
                  iconSize={20}
                />
                <Tooltip title="Remove zone">
                  <IconButton size="small" onClick={() => handleDelete(z)}>
                    <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          )}
        </Box>
      </Box>

      <div className="Dialog_Footer" style={{ marginTop: 20 }}>
        <Button onClick={onClose} className="Dialog_Cancel">
          Close
        </Button>
      </div>
    </Box>
  );
};

export default AddZoneDialog;
